/* eslint-disable no-console */
/* eslint-disable arrow-body-style */
/* eslint-disable import/no-extraneous-dependencies */
import { Router } from '@abyss/web/ui/Router';
import { useCountryStatesSearch } from '@src//hooks/useCountryStatesSearch';
import { useSearch } from '@src/hooks/providers/ffc/useSearch';
import { useDecisionCodes } from '@src/hooks/useDecisionCodes';
import { useDecisionReasons } from '@src/hooks/useDecisionReasons';
import { useProviderIdentifierTypesSearch } from '@src/hooks/useProviderIdentifierTypesSearch';
import { useProviderTypesSearch } from '@src/hooks/useProviderTypesSearch';
import { AdminPage } from '@src/routes/admin';
import { HomePage } from '@src/routes/home';
import { FFCProviderCreatePage } from '@src/routes/provider/ffc/create';
import { ManageFFCCasesPage } from '@src/routes/provider/ffc/manage';
import { LoadingPage } from '@src/routes/provider/ffc/manage/LoadingPage';
import { FFCProviderUpdatePage } from '@src/routes/provider/ffc/update';
import { Filter } from '@src/routes/provider/global/search/Filter';
import { Results } from '@src/routes/provider/global/search/Results';
import React, { useEffect, useState } from 'react';
import { v4 as someRandomState } from 'uuid';
import { useLocation } from 'react-router-dom';
import { RouteUrl } from '@src/common/util/routeUrl';
import { useAuthState } from '@src/app/auth/auth.state';
import { getCachedDate } from '@src/common/util/dates';
import { getReasonPhrase } from 'http-status-codes';
import { RiskScoresPage } from '@src/routes/risk-scores/components/RiskScoresPage';
import { AppBody } from '../app/AppBody';
import { utils } from './provider/common/dataGridUtils';
import { Page404NotFound } from './Page404NotFound';
import { publishPageHitToNess } from '../common/logging/publishNessEvent';
import { EventSearchPage } from './events/search/EventSearchPage';
import { LinkAnalysisPage } from './link-analysis/components/LinkAnalysisPage';

export const Routes = () => {
  const {
    canReadFfc,
    canWriteFfc,
    canSearchEvents,
    canSearchGraph,
    canRiskScore,
  } = useAuthState((state) => state.permissions);
  // capture what page we happen to be on
  const location = useLocation();
  useEffect(() => {
    // when the user changes page then we should fire the ness log
    publishPageHitToNess(location.pathname).catch((reason) =>
      console.error(reason?.message)
    );
  }, [location.pathname]);

  const [genRandomState, setGenRandomState] = useState(someRandomState());
  const [selectOptionsPropsState, setSelectOptionsPropsState] = useState({});

  const allStatePropsLoaded = (props) => {
    return (
      'decisionCodes' in props &&
      'decisionReasons' in props &&
      'providerTypes' in props &&
      'states' in props &&
      'providerIdentifierTypes' in props
    );
  };

  const [, getDecisionCodes] = useDecisionCodes({
    onCompleted: (response) => {
      try {
        const dc = response?.data?.decisionCodes?.toSorted().map((dataItem) => {
          return {
            value: dataItem.code,
            label: dataItem.name,
          };
        });

        setSelectOptionsPropsState((options) => {
          return {
            ...options,
            decisionCodes: dc,
          };
        });
      } catch (e) {
        console.log('decision-codes:Exception:%s', e);
      }
    },
  });

  const [, getDecisionReasons] = useDecisionReasons({
    onCompleted: (response) => {
      try {
        const dr = response?.data?.decisionReasons
          ?.toSorted()
          .map((dataItem) => {
            return {
              value: dataItem.code,
              label: dataItem.name,
            };
          });
        utils.setAllowedReasonsCodes(
          'ffc',
          dr.map((value) => value.value)
        );
        setSelectOptionsPropsState((options) => {
          return {
            ...options,
            decisionReasons: dr,
          };
        });
      } catch (e) {
        console.log('decision-reasons:Exception:%s', e);
      }
    },
  });

  const [, getProviderTypes] = useProviderTypesSearch({
    onCompleted: (response) => {
      try {
        const ptypes = response.data.providerTypes?.content
          ?.toSorted()
          .map((dataItem) => {
            return {
              value: dataItem.code,
              label: dataItem.type,
            };
          });

        setSelectOptionsPropsState((options) => {
          return {
            ...options,
            providerTypes: ptypes,
          };
        });
      } catch (e) {
        console.log('types:Exception:%s', e);
      }
    },
  });

  const [, getCountryStates] = useCountryStatesSearch({
    onCompleted: (response) => {
      try {
        const s = response?.data?.countryStates?.content
          ?.toSorted()
          .map((dataItem) => {
            return {
              value: dataItem.code,
              label: dataItem.name,
            };
          });
        setSelectOptionsPropsState((options) => {
          return {
            ...options,
            states: s,
          };
        });
      } catch (e) {
        console.log('states:Exception:%s', e);
      }
    },
  });

  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const [providers, setProviders] = useState([]);
  const [, getProviderSearch] = useSearch({
    onCompleted: (response) => {
      const { status, data } = response.data.search;

      if (status === 200) {
        const ffcProviders = (data || []).map((provider) => {
          return {
            ...provider,
            presentedDate: getCachedDate(provider.presentedDate),
          };
        });

        setProviders(ffcProviders);
        setProvidersLoaded(true);
        setIsLoadingProviders(false);
      } else {
        // got an error
        const error = response.data.search;
        const reason = getReasonPhrase(error?.status);
        console.error(`Error retrieving FFC providers:${reason}`, error);
      }
    },
    onError: (err) => {
      console.error(`Error retrieving FFC providers`, err);
      setProviders([]);
      setProvidersLoaded(true);
      setIsLoadingProviders(false);
    },
  });

  const [, getProviderIdentifierTypes] = useProviderIdentifierTypesSearch({
    onCompleted: (response) => {
      try {
        // for now, we'll check against these but ideally the api should filter out non ffc allowed types
        const FFC_ALLOWED_TYPES = ['MPIN', 'NPI', 'TIN'];
        const pits = response.data?.providerIdentifierTypes
          ?.toSorted()
          .filter((t) => FFC_ALLOWED_TYPES.includes(t.code))
          .map((dataItem) => {
            return {
              value: dataItem.code,
              label: dataItem.code,
            };
          });
        setSelectOptionsPropsState((options) => {
          return {
            ...options,
            providerIdentifierTypes: pits,
          };
        });
      } catch (e) {
        console.error('provider-identifier-types:Exception:%s', e);
      }
    },
  });

  const [globalSearchFilterProps, setGlobalSearchFilterProps] = useState(null);
  const tiggerUpdatedGlobalSearchFilterProps = (formData) => {
    setGlobalSearchFilterProps(formData);
  };

  const tiggerSearch = () => {
    // eslint-disable-next-line arrow-body-style
    setGenRandomState(someRandomState());
  };
  /**
    when the user edits/updates a provider this is called so that we update the state rather
    than making a full-blown call-out to backend again */
  const tiggerUpdatedProvider = (updatedProvider) => {
    // eslint-disable-next-line arrow-body-style
    // find where in the collection the updated provider sits
    const index = providers.findIndex((p) => p.id === updatedProvider.id);
    // if we can't find it then do a call-out to backend
    if (index === -1) {
      // this will force the state to change and call the `useEffect()` which will call the search api
      setGenRandomState(someRandomState());
    } else {
      // ok, so we have an existing provider, now update it
      providers.splice(index, 1, {
        id: updatedProvider.id,
        dollarBills: updatedProvider.dollarBills,
        firstName: updatedProvider.firstName,
        lastName: updatedProvider.lastName,
        middleInitial: updatedProvider.middleInitial,
        notes: updatedProvider.notes,
        orgName: updatedProvider.orgName,
        presentedDate: updatedProvider.presentedDate,
        providerTypeCode: updatedProvider.providerTypeCode,
        requestedDecisionCode: updatedProvider.requestedDecisionCode,
        tipSource: updatedProvider.tipSource,
      });
      // and set the state
      setProviders(providers);
    }
  };

  useEffect(() => {
    // only do this once
    getDecisionCodes();
    getDecisionReasons();
    getProviderTypes();
    getProviderIdentifierTypes();
    getCountryStates();
  }, []);

  useEffect(() => {
    if (canReadFfc || canWriteFfc) {
      setProvidersLoaded(false);
      setIsLoadingProviders(true);
      getProviderSearch({
        variables: {
          pageNumber: 0,
          pageSize: 3000,
        },
      });
    } else {
      setProvidersLoaded(true);
      setIsLoadingProviders(false);
    }
  }, [canReadFfc, canWriteFfc, genRandomState]);

  const renderWithAccess = () => {
    return (
      <Router.Routes title="dFEDS">
        <Router.Route path={RouteUrl.home} element={<AppBody />}>
          <Router.Route path={RouteUrl.admin} element={<AdminPage />} />
          {/* Home page */}
          <Router.Route path={RouteUrl.home} element={<HomePage />} />
          {/* The global search */}
          <Router.Route
            path={RouteUrl.providerSearch}
            element={
              <Filter
                previousFormData={globalSearchFilterProps}
                triggerFormDataUpdate={tiggerUpdatedGlobalSearchFilterProps}
                props={selectOptionsPropsState}
              />
            }
          />
          <Router.Route
            path={RouteUrl.providerSearchResults}
            element={
              <Results
                filterData={globalSearchFilterProps}
                props={selectOptionsPropsState}
              />
            }
          />
          {/* manage FFC Provider page - if a refresh is called then we may need to display a loading sign */}
          {
            // (canReadFfc || canWriteFfc ) && (
            providersLoaded ? (
              <Router.Route
                path={RouteUrl.providerManage}
                element={
                  <ManageFFCCasesPage
                    providers={providers}
                    selectOptionsProps={selectOptionsPropsState}
                  />
                }
              />
            ) : (
              <Router.Route
                path={RouteUrl.providerManage}
                element={
                  <LoadingPage
                    title="Loading Providers"
                    message="Please wait while we load your providers list......"
                    isLoading={isLoadingProviders}
                  />
                }
              />
            )
            // )
          }
          {/* FFC Provider create page - the grid doesn't remember the states between client refresh hence we need to wait for states */}
          {canWriteFfc && (
            <React.Fragment>
              {allStatePropsLoaded(selectOptionsPropsState) ? (
                <Router.Route
                  path={RouteUrl.providerCreate}
                  element={
                    <FFCProviderCreatePage
                      triggerSearch={tiggerSearch}
                      tiggerUpdatedProvider={tiggerUpdatedProvider}
                      selectOptionsProps={selectOptionsPropsState}
                    />
                  }
                />
              ) : (
                <Router.Route
                  path={RouteUrl.providerCreate}
                  element={
                    <LoadingPage
                      title="Refreshing details"
                      message="Please wait while we refresh the screeen..."
                      isLoading={!allStatePropsLoaded(selectOptionsPropsState)}
                    />
                  }
                />
              )}
            </React.Fragment>
          )}
          {/* FFC Provider edit/update page */}
          {(canReadFfc || canWriteFfc) && (
            <React.Fragment>
              {allStatePropsLoaded(selectOptionsPropsState) ? (
                <Router.Route
                  path={RouteUrl.providerEdit}
                  element={
                    <FFCProviderUpdatePage
                      tiggerUpdatedProvider={tiggerUpdatedProvider}
                      selectOptionsProps={selectOptionsPropsState}
                    />
                  }
                />
              ) : (
                <Router.Route
                  path={RouteUrl.providerEdit}
                  element={
                    <LoadingPage
                      title="Refreshing details"
                      message="Please wait while we refresh the screeen..."
                      isLoading={!allStatePropsLoaded(selectOptionsPropsState)}
                    />
                  }
                />
              )}
            </React.Fragment>
          )}
          {canSearchEvents && (
            <Router.Route
              path={RouteUrl.eventsSearch}
              element={<EventSearchPage />}
            />
          )}
          {canSearchGraph && (
            <Router.Route
              path={RouteUrl.linkAnalysis}
              element={<LinkAnalysisPage />}
            />
          )}
          {canRiskScore && (
            <Router.Route
              path={RouteUrl.riskScores}
              element={<RiskScoresPage />}
            />
          )}
          {/* if the path is unknown to us then go back to the 404 page */}
          <Router.Route path="/*" element={<Page404NotFound />} />
        </Router.Route>
      </Router.Routes>
    );
  };

  return renderWithAccess();
};
